import type { CommendaAiUploadFile } from '@commenda/api/commenda-ai/types';

export class ChatUploadHelper {
  static fromFileList(fileList: FileList): CommendaAiUploadFile[] {
    return [...fileList]
      .map((file) => ({ file, path: file.webkitRelativePath || file.name }))
      .filter(
        ({ file, path }) =>
          !file.webkitRelativePath || ChatUploadHelper.isVisible(path)
      );
  }

  static readDrop(
    dataTransfer: DataTransfer,
    canUploadFolders: boolean
  ): Promise<CommendaAiUploadFile[]> {
    if (!canUploadFolders)
      return Promise.resolve(
        [...dataTransfer.files].map((file) => ({ file, path: file.name }))
      );

    const entries = [...dataTransfer.items]
      .map((item) => item.webkitGetAsEntry())
      .filter((entry): entry is FileSystemEntry => entry !== null);

    if (entries.length === 0)
      return Promise.resolve(ChatUploadHelper.fromFileList(dataTransfer.files));

    return Promise.all(
      entries.map((entry) => ChatUploadHelper.readEntry(entry))
    ).then((files) => ChatUploadHelper.withoutHidden(files.flat()));
  }

  private static readEntry(
    entry: FileSystemEntry
  ): Promise<CommendaAiUploadFile[]> {
    if (ChatUploadHelper.isFileEntry(entry))
      return new Promise((resolve, reject) => {
        entry.file(
          (file) => resolve([{ file, path: entry.fullPath.slice(1) }]),
          reject
        );
      });

    if (!ChatUploadHelper.isDirectoryEntry(entry)) return Promise.resolve([]);

    return ChatUploadHelper.readAllEntries(entry.createReader())
      .then((children) =>
        Promise.all(children.map((child) => ChatUploadHelper.readEntry(child)))
      )
      .then((files) => files.flat());
  }

  private static isFileEntry(
    entry: FileSystemEntry
  ): entry is FileSystemFileEntry {
    return entry.isFile;
  }

  private static isDirectoryEntry(
    entry: FileSystemEntry
  ): entry is FileSystemDirectoryEntry {
    return entry.isDirectory;
  }

  private static readAllEntries(
    reader: FileSystemDirectoryReader,
    read: FileSystemEntry[] = []
  ): Promise<FileSystemEntry[]> {
    return new Promise<FileSystemEntry[]>((resolve, reject) => {
      reader.readEntries(resolve, reject);
    }).then((batch) =>
      batch.length === 0
        ? read
        : ChatUploadHelper.readAllEntries(reader, [...read, ...batch])
    );
  }

  private static withoutHidden(
    files: CommendaAiUploadFile[]
  ): CommendaAiUploadFile[] {
    return files.filter(({ path }) => ChatUploadHelper.isVisible(path));
  }

  private static isVisible(path: string): boolean {
    return !path.split('/').some((segment) => segment.startsWith('.'));
  }
}
